import { ClockCircleTwoTone, FlagTwoTone, LockFilled, WarningTwoTone } from "@ant-design/icons";
import { Box } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Col, Row, Space, Tooltip } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { FilterValue } from "antd/lib/table/interface";
import FriendlyIdFilter from "components/staff/Packages/FriendlyIdFilter";
import { StaffPackagesFilterDetail } from "components/staff/Packages/StaffPackagesFilter";
import EditListingModal from "components/staff/PendingListings/EditListingModal";
import PendingListingsToolbar, { BulkType } from "components/staff/PendingListings/PendingListingsToolbar";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import { InitialPageRequest, PagedResultWithExtra, PageRequest } from "models/common";
import { ListingDuration, ListingDurationNames, PendingListing } from "models/pendingListings";
import { FC, Fragment, Key, PropsWithChildren, useState } from "react";
import { localizedDate, pageRequestForPost, pageRequestToUrlSearchParams } from "util/helpers";
import { createCustomFilter } from "util/table";

export interface PendingUploadExtras {
    uploadCountTonight: number;
    enqueuedCount: number;
    pendingCount: number;
}

const usePendingListings = (pageRequest: PageRequest) => {
    const options = pageRequestToUrlSearchParams(pageRequest);

    const getListings = useAuthenticatedRequest<PagedResultWithExtra<PendingListing, PendingUploadExtras>>(({
        method: "get",
        url: `/pending-listings?${options}`
    }));

    return useQuery(["pending-listings", pageRequest], getListings);
}

const useBulkActions = () => {
    const cardUploadDate = useAuthenticatedRequestCreator<void, { uploadDate: string | null, cardIds?: string[], pageRequest?: PageRequest }>(
        (data) => ({
            method: 'post',
            url: '/pending-listings/card-upload-date',
            data
        })
    );

    const packageUploadDate = useAuthenticatedRequestCreator<void, { uploadDate: string | null, packageIds: string[] }>(
        (data) => ({
            method: "post",
            url: "/pending-listings/package-upload-date",
            data
        })
    );

    const durationChange = useAuthenticatedRequestCreator<void, { duration: ListingDuration, cardIds?: string[], pageRequest?: PageRequest }>(
        (data) => ({
            method: "post",
            url: "/pending-listings/update-duration",
            data
        })
    );

    const { mutateAsync: changeCardUploadDates, isLoading: isUpdatingCards } = useMutation(cardUploadDate);
    const { mutateAsync: changePackageUploadDates, isLoading: isUpdatingPackages } = useMutation(packageUploadDate);
    const { mutateAsync: changeListingDuration, isLoading: isUpdatingDuration } = useMutation(durationChange);

    return {
        changeCardUploadDates,
        changePackageUploadDates,
        changeListingDuration,
        isUpdating: isUpdatingCards || isUpdatingPackages || isUpdatingDuration
    };
}

const BigIcon: FC<PropsWithChildren & { title: string }> = ({ children, title }) =>
    <div style={{ fontSize: '28px' }}>
        <Tooltip title={title}>
            {children}
        </Tooltip>
    </div>;

const StatusIcon: FC<PendingListing> = ({ listingErrors, bulkExempt }) => {
    const BulkExempt = bulkExempt
        ? <FlagTwoTone title="Exempt from Bulk Actions" twoToneColor="#9214fa" />
        : null;

    if (listingErrors.length > 0) {
        return <Space>
            <BigIcon title={`Listing Errors!`}>
                <WarningTwoTone twoToneColor="#faad14" />
            </BigIcon>
            {BulkExempt}
        </Space>;
    }

    return <Space>
        <BigIcon title="Listing Pending">
            <ClockCircleTwoTone />
        </BigIcon>
        {BulkExempt}
    </Space>;
}

const PendingListings = () => {
    const [{ page, pageSize, withinDays, sort, filterBy }, setPageRequest] =
        useState<PageRequest>({ ...InitialPageRequest, pageSize: 30, filterBy: {} });
    const [filterDetail, setFilterDetail] = useState<StaffPackagesFilterDetail>({});
    const { data: resp, isLoading, refetch } = usePendingListings({
        page,
        filterBy,
        withinDays,
        sort,
        pageSize
    });
    const { data, totalRecords = 0, extra } = resp || {};
    const [selectedCardIds, setSelectedCardIds] = useState<Key[]>([]);
    const [packageTypeId, setPackageTypeId] = useState<string | null>(null);
    const { changeCardUploadDates, changePackageUploadDates, changeListingDuration, isUpdating } = useBulkActions();

    const working = isLoading || isUpdating;
    const isFiltered = packageTypeId !== null || !!filterDetail.friendlyId;

    const onChangedCardUploadDates = async (uploadDate: string | null, type: BulkType) => {
        if (type === BulkType.Selection) {
            await changeCardUploadDates({
                uploadDate,
                cardIds: selectedCardIds.map(id => id.toString())
            });
        } else {
            await changeCardUploadDates({
                uploadDate,
                pageRequest: pageRequestForPost({ page, filterBy, withinDays, sort })
            });
        }

        setSelectedCardIds([]);
        await refetch();
    };

    const onChangedPackageUploadDates = async (uploadDate: string, packageIds: string[]) => {
        await changePackageUploadDates({
            uploadDate,
            packageIds
        });

        await refetch();
    }

    const onDurationChanged = async (duration: ListingDuration, type: BulkType) => {
        if (type === BulkType.Selection) {
            await changeListingDuration({
                duration,
                cardIds: selectedCardIds.map(id => id.toString())
            });
        } else {
            await changeListingDuration({
                duration,
                pageRequest: pageRequestForPost({ page, filterBy, withinDays, sort })
            });
        }

        setSelectedCardIds([]);
        await refetch();
    }

    const tableChanged = (
        // pagination
        { current: newPage, pageSize: newPageSize }: any,
        // filtering
        filters: Record<string, FilterValue | null>,
        // sorting
        { column, field, order }: any
    ) => {
        setPageRequest({
            page: newPageSize !== pageSize ? 1 : newPage,
            pageSize: newPageSize,
            filterBy: { ...filterBy, ...filters },
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
    };

    const onFilterUpdate = (key: string) => (value: any) => {
        setFilterDetail((prev) => ({ ...prev, [key]: value }));
    };

    const clearFilter = () => {
        setFilterDetail({});
        setPageRequest({
            page: 1,
            pageSize: pageSize,
            filterBy: {},
            withinDays,
            sort
        });
        setPackageTypeId(null);
    }

    const onPackageTypeChanged = (packageTypeId: string | null) => {
        delete filterBy!["PackageTypeId"];
        setPageRequest({
            page: 1,
            pageSize,
            withinDays,
            filterBy: packageTypeId !== null
                ? { ...filterBy, "PackageTypeId": [packageTypeId] }
                : { ...filterBy },
            sort
        });
        setPackageTypeId(packageTypeId);
    }

    const columns: ColumnsType<PendingListing> = [
        {
            title: "Status",
            key: "statusIcon",
            render: r => <StatusIcon {...r} />
        },
        {
            title: "Package ID",
            dataIndex: "friendlyPackageId",
            ...createCustomFilter(
                FriendlyIdFilter,
                onFilterUpdate("friendlyId"),
                filterDetail.friendlyId
            ),
        },
        {
            title: "Card Title",
            dataIndex: "title"
        },
        {
            title: "SKU",
            dataIndex: "sku"
        },
        {
            title: "Upload Date",
            dataIndex: "uploadDate",
            render: localizedDate
        },
        {
            title: "Duration",
            dataIndex: "duration",
            render: (v: ListingDuration) => ListingDurationNames[v]
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, r) => r.locked
                ? (
                    <BigIcon title="Upload In Progress">
                        <LockFilled style={{ marginLeft: '40px', color: 'var(--dcs-error-red)' }} />
                    </BigIcon>
                ) : (
                    <EditListingModal
                        cardId={r.cardId}
                        sku={r.sku}
                        onSaved={async (newTitle) => {
                            if (newTitle !== r.title) {
                                await refetch();
                            }
                        }}
                        onDoNotUpload={async () => { await refetch(); }}
                    />
                )
        }
    ];

    return <Box p={50} w='100%'>
        <Row align="bottom">
            <Col span={24}>
                <Box fontSize={36}>Pending eBay Listings</Box>
            </Col>
        </Row>
        <Table
            key={(data || []).length * page}
            size="small"
            title={() => <Space>
                <PendingListingsToolbar
                    selectedRowCount={selectedCardIds.length}
                    filteredRowCount={isFiltered ? totalRecords : 0}
                    onChangedCardUploadDates={onChangedCardUploadDates}
                    onChangedPackageUploadDates={onChangedPackageUploadDates}
                    onDurationChanged={onDurationChanged}
                    onPackageTypeChanged={onPackageTypeChanged}
                    pendingUploadExtra={extra}
                    packageTypeId={packageTypeId}
                    friendlyIdFilter={filterDetail.friendlyId}
                    onFilterClear={clearFilter}
                />
            </Space>}
            loading={working}
            columns={columns}
            dataSource={data}
            rowKey={l => l.cardId}
            onChange={tableChanged}
            rowSelection={{
                selectedRowKeys: selectedCardIds,
                onChange: setSelectedCardIds,
                preserveSelectedRowKeys: true,
                getCheckboxProps: rec => ({
                    disabled: rec.locked
                })
            }}
            pagination={{
                total: totalRecords,
                current: page,
                pageSize,
                pageSizeOptions: [30, 50, 100],
                showSizeChanger: true,
                showQuickJumper: true,
                position: ['topRight', 'bottomRight']
            }}
            expandable={{
                rowExpandable: r => r.listingErrors.length > 0,
                expandedRowRender: r => (
                    <Row style={{ color: 'var(--dcs-error-red)' }} gutter={[0, 12]}>
                        {r.listingErrors.map(e => <Fragment key={r.sku}>
                            <Col span={1} offset={1}>{e.code}</Col>
                            <Col span={22}>{e.message}</Col>
                        </Fragment>)}
                    </Row>
                ),
                defaultExpandAllRows: true
            }}
        />
    </Box>;
}

export default PendingListings;